import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { DateTimePickerAndroid } from "@react-native-community/datetimepicker";
import { format } from "date-fns";
import { useFocusEffect, useNavigation } from "@react-navigation/native";

import { DivineBackground } from "../../../shared/ui/DivineBackground";
import { Header } from "../../../shared/ui/Header";
import { Button } from "../../../shared/ui/FormElements";
import { Flash } from "../../../shared/utils/Flash";
import { ManglaAartiService } from "../../../services/ManglaAartiService";
import type { ManglaAartiModel, ManglaAartiTotalModel } from "../../../models/ManglaAartiModel";
import { useAuth } from "../../../../context/AuthContext";

export function ManglaAartiScreen() {
  const navigation = useNavigation<any>();
  const { user } = useAuth();

  const todayStr = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);
  const [date, setDate] = useState(todayStr);

  const [temple, setTemple] = useState(false);
  const [homeBefore5, setHomeBefore5] = useState(false);
  const [homeAfter5, setHomeAfter5] = useState(false);
  const [isEditable, setIsEditable] = useState(true);

  const [marks, setMarks] = useState(0);
  const [totalMarks, setTotalMarks] = useState(0);
  const [manglaAartiId, setManglaAartiId] = useState<string | null>(null);

  const [loading, setLoading] = useState(false);

  // marks calc
  useEffect(() => {
    let m = 0;
    if (temple) m += ManglaAartiService.MANGLA_AARTI.TEMPLE;
    if (homeBefore5) m += ManglaAartiService.MANGLA_AARTI.HOME_BEFORE_5;
    if (homeAfter5) m += ManglaAartiService.MANGLA_AARTI.HOME_AFTER_5;
    setMarks(m);
  }, [temple, homeBefore5, homeAfter5]);

  // load against date
  useFocusEffect(
    useCallback(() => {
      loadAgainstDate(date);   // refresh when coming back from history
    }, [date])
  );

  const openDatePicker = () => {
    DateTimePickerAndroid.open({
      value: new Date(date),
      mode: "date",
      maximumDate: new Date(),
      onChange: (event, selectedDate) => {
        if (event.type === "set" && selectedDate) {
          setDate(format(selectedDate, "yyyy-MM-dd"));
        }
      },
    });
  };

  const loadAgainstDate = async (dateStr: string) => {
    try {
      setLoading(true);

      const res = await ManglaAartiService.getAgainstDate({ date: dateStr });

      // ✅ adjust if ResponseModel keys differ
      if (res?.status != 200) {
        Flash.error(res?.message || "Failed to load");
        return;
      }


      const data = res.data as ManglaAartiTotalModel | null;
      setTotalMarks(data?.totalMarks ?? 0);

      const d = data?.manglaAartiData;

      if (d) {
        setManglaAartiId(d.manglaAartiId ?? null);
        setTemple(!!d.temple);
        setHomeBefore5(!!d.homeBefore5);
        setHomeAfter5(!!d.homeAfter5);
        setIsEditable(d.isEditable);
      } else {
        setManglaAartiId(null);
        setTemple(false);
        setHomeBefore5(false);
        setHomeAfter5(false);
        setIsEditable(true);
      }
    } catch (e: any) {
      Flash.error(e?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const onSave = async () => {
    try {
      if (!user?.userId) {
        Flash.error("User not found");
        return;
      }

      setLoading(true);

      const payload: ManglaAartiModel = {
        manglaAartiId: manglaAartiId ?? null,
        userId: user.userId,
        date,
        temple,
        homeBefore5,
        homeAfter5,
        marks,
        isEditable
      };

      const res = await ManglaAartiService.saveManglaAarti(payload);

      if (res?.status != 200) {
        Flash.error(res?.message || "Failed to save");
        return;
      }

      Flash.success("Success!", "Mangla Aarti Saved Successfully!");
      // await loadAgainstDate(date);
      navigation.navigate("ManglaAartiHistory");

    } catch (e: any) {
      Flash.error(e?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const ToggleRow = ({
    label,
    rowMarks,
    checked,
    onToggle,
  }: {
    label: string;
    rowMarks: number;
    checked: boolean;
    onToggle: () => void;
  }) => (
    <Pressable
      onPress={onToggle}
      style={({ pressed }) => [
        styles.toggleRow,
        checked ? styles.toggleRowActive : styles.toggleRowInactive,
        pressed ? { opacity: 0.95 } : null,
      ]}
    >
      <View style={styles.toggleLeft}>
        <View style={[styles.circle, checked ? styles.circleActive : styles.circleInactive]}>
          <Text style={[styles.circleIcon, { color: checked ? "#fff" : "rgba(74,35,17,0.35)" }]}>✓</Text>
        </View>

        <Text style={[styles.toggleText, { color: checked ? "#4A2311" : "rgba(74,35,17,0.65)" }]}>
          {label}
        </Text>
      </View>

      <View style={styles.marksPill}>
        <Text style={styles.marksPillText}>{rowMarks} Marks</Text>
      </View>
    </Pressable>
  );

  return (
    <DivineBackground overlayOpacity={0.9}>
      <View style={{ flex: 1 }}>
        <Header title="Mangla Aarti" showBack />

        <ScrollView contentContainerStyle={styles.container} showsVerticalScrollIndicator={false}>
          {/* Summary */}
          <View style={styles.summaryCard}>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryTitle}>This Month's Offering</Text>
              <View style={{ alignItems: "flex-end" }}>
                <Text style={styles.totalLabel}>TOTAL MARKS</Text>
                <Text style={styles.totalValue}>{totalMarks}</Text>
              </View>
            </View>
          </View>

          {/* Form Card */}
          <View style={styles.card}>
            <View style={styles.topRow}>
              <Pressable onPress={openDatePicker} style={styles.dateChip}>
                <Text style={styles.dateChipIcon}>📅</Text>
                <Text style={styles.dateChipText}>{format(new Date(date), "dd-MM-yyyy")}</Text>
              </Pressable>

              <Pressable
                onPress={() => navigation.navigate("ManglaAartiHistory")}
                style={styles.historyBtn}
              >
                <Text style={styles.historyIcon}>🕘</Text>
                <Text style={styles.historyText}>History</Text>
              </Pressable>
            </View>

            <View style={{ gap: 10, marginTop: 14 }}>
              <ToggleRow
                label="At Temple"
                rowMarks={ManglaAartiService.MANGLA_AARTI.TEMPLE}
                checked={temple}
                onToggle={() => setTemple((p) => !p)}
              />
              <ToggleRow
                label="Home Before 5 AM"
                rowMarks={ManglaAartiService.MANGLA_AARTI.HOME_BEFORE_5}
                checked={homeBefore5}
                onToggle={() => setHomeBefore5((p) => !p)}
              />
              <ToggleRow
                label="Home After 5 AM"
                rowMarks={ManglaAartiService.MANGLA_AARTI.HOME_AFTER_5}
                checked={homeAfter5}
                onToggle={() => setHomeAfter5((p) => !p)}
              />

              <View style={styles.calcRow}>
                <Text style={styles.calcLabel}>Calculated Marks:</Text>
                <Text style={styles.calcValue}>{marks}</Text>
              </View>
            </View>

            {isEditable && (
              <View style={styles.btnRow}>
                <Pressable
                  onPress={() => navigation.goBack()}
                  style={({ pressed }) => [styles.cancelBtn, pressed ? { opacity: 0.95 } : null]}
                  disabled={loading}
                >
                  <Text style={styles.cancelText}>Cancel</Text>
                </Pressable>

                <View style={{ flex: 1 }}>
                  <Button title={loading ? "Saving..." : "Save"} onPress={onSave} disabled={loading} />
                </View>
              </View>
            )}

            {loading ? (
              <View style={{ marginTop: 12, alignItems: "center" }}>
                <ActivityIndicator />
              </View>
            ) : null}
          </View>
        </ScrollView>
      </View>
    </DivineBackground>
  );
}

const styles = StyleSheet.create({
  container: { padding: 14, paddingBottom: 30, gap: 12 },

  summaryCard: {
    borderRadius: 14,
    padding: 14,
    backgroundColor: "rgba(255,255,255,0.75)",
    borderWidth: 1,
    borderColor: "rgba(251,146,60,0.25)",
    shadowColor: "#000",
    shadowOpacity: 0.10,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 6 },
    elevation: 3,
  },
  summaryRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "center" },
  summaryTitle: { fontSize: 13, fontWeight: "900", color: "#4A2311" },
  totalLabel: { fontSize: 10, color: "rgba(74,35,17,0.45)", fontWeight: "800", letterSpacing: 0.8 },
  totalValue: { fontSize: 26, fontWeight: "900", color: "#F97316" },

  card: {
    borderRadius: 16,
    padding: 14,
    backgroundColor: "rgba(255,255,255,0.88)",
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.06)",
    shadowColor: "#000",
    shadowOpacity: 0.12,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 8 },
    elevation: 4,
  },

  topRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "center" },

  dateChip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: "rgba(255,238,210,0.85)",
    borderWidth: 1,
    borderColor: "rgba(251,146,60,0.28)",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 999,
  },
  dateChipIcon: { fontSize: 14 },
  dateChipText: { fontSize: 13, fontWeight: "800", color: "#4A2311" },

  historyBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 10,
    height: 34,
    borderRadius: 10,
    backgroundColor: "rgba(255,255,255,0.55)",
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.06)",
  },
  historyIcon: { fontSize: 14 },
  historyText: { fontSize: 12, fontWeight: "800", color: "rgba(74,35,17,0.75)" },

  toggleRow: {
    borderRadius: 14,
    padding: 12,
    borderWidth: 1,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  toggleRowInactive: {
    backgroundColor: "rgba(255,255,255,0.55)",
    borderColor: "rgba(0,0,0,0.06)",
  },
  toggleRowActive: {
    backgroundColor: "rgba(255,238,210,0.65)",
    borderColor: "rgba(251,146,60,0.35)",
  },
  toggleLeft: { flexDirection: "row", alignItems: "center", gap: 10 },

  circle: {
    width: 34,
    height: 34,
    borderRadius: 999,
    alignItems: "center",
    justifyContent: "center",
  },
  circleInactive: { backgroundColor: "rgba(74,35,17,0.08)" },
  circleActive: { backgroundColor: "#F97316" },
  circleIcon: { fontSize: 16, fontWeight: "900" },

  toggleText: { fontSize: 14, fontWeight: "900" },

  marksPill: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: "rgba(255,255,255,0.9)",
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.06)",
    borderRadius: 10,
  },
  marksPillText: { fontSize: 11, fontWeight: "800", color: "rgba(74,35,17,0.55)" },

  calcRow: {
    marginTop: 6,
    borderRadius: 14,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: "rgba(255,238,210,0.55)",
    borderWidth: 1,
    borderColor: "rgba(251,146,60,0.22)",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  calcLabel: { fontSize: 13, fontWeight: "800", color: "rgba(74,35,17,0.65)" },
  calcValue: { fontSize: 20, fontWeight: "900", color: "#F97316" },

  btnRow: { flexDirection: "row", gap: 10, marginTop: 14 },
  cancelBtn: {
    flex: 1,
    height: 46,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.12)",
    backgroundColor: "rgba(255,255,255,0.9)",
    alignItems: "center",
    justifyContent: "center",
  },
  cancelText: { fontSize: 14, fontWeight: "900", color: "rgba(74,35,17,0.60)" },
});